import { memo } from "react";
import { Check, FileText, ShoppingBag } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { DOCUMENT_TYPE_OPTIONS } from "../constants";
import {
  FISCAL_DOCUMENT_TYPE,
  type FiscalDocumentType,
} from "../types/fiscal-export.types";

const ICONS = {
  [FISCAL_DOCUMENT_TYPE.NFSE]: FileText,
  [FISCAL_DOCUMENT_TYPE.NFCE]: ShoppingBag,
} as const;

interface DocumentTypeSelectorProps {
  selected: FiscalDocumentType[];
  disabled?: boolean;
  onToggle: (type: FiscalDocumentType) => void;
}

export const DocumentTypeSelector = memo(function DocumentTypeSelector({
  selected,
  disabled,
  onToggle,
}: DocumentTypeSelectorProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {DOCUMENT_TYPE_OPTIONS.map((option) => {
        const Icon = ICONS[option.type];
        const isSelected = selected.includes(option.type);

        return (
          <button
            key={option.type}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onToggle(option.type)}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
              "disabled:opacity-60 disabled:cursor-not-allowed",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border hover:border-muted-foreground/40",
            )}
          >
            <span
              className={cn(
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                isSelected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-muted-foreground/40",
              )}
            >
              {isSelected && <Check className="h-3.5 w-3.5" />}
            </span>

            <span className="min-w-0 space-y-1">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Icon className="h-4 w-4 text-muted-foreground" />
                {option.label}
              </span>
              <span className="block text-xs text-muted-foreground">
                {option.description}
              </span>
              <span className="block font-mono text-xs text-muted-foreground/80">
                {option.folder}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
});
